import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Input, MultiSelect, Select } from "@livechat/design-system";
import {
  DesignToken,
  RadiusToken,
  ShadowToken,
  SpacingToken,
  TransitionDurationToken,
  TransitionTimingToken,
} from "@livechat/design-system-react-components";

import TextAreaAutosizeField from "components/TextAreaAutosizeField";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { withVar } from "themes/helpers";

import { WorkflowPartGridType } from "../interfaces";
import { ContentWrapper } from "../styled";

export const StyledContentWrapper = styled(ContentWrapper)`
  padding-bottom: 120px;
`;

export const WorkflowsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, auto));
  grid-gap: var(${SpacingToken.Spacing4});
  margin: var(${SpacingToken.Spacing4}) 0;
`;

export const EntryContainer = styled.div`
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  padding: ${withVar(SpacingToken.Spacing4)};
  &:hover {
    cursor: pointer;
    border: 1px solid ${withVar(DesignToken.BorderBasicHover)};
  }
  &:focus {
    box-shadow: ${withVar(ShadowToken.Focus)};
  }

  &[data-error="true"] {
    background-color: ${withVar(DesignToken.SurfaceAccentEmphasisMinNegative)};
    border-color: ${withVar(DesignToken.BorderBasicNegative)};
    &:hover {
      background-color: ${withVar(DesignToken.SurfaceAccentEmphasisMinNegative)};
    }
  }
`;

export const SinglePartEditorBox = styled.div`
  &:first-of-type {
    margin-top: 0.857em;
  }

  margin: 0.571em 0;
`;

export const WorkflowPartEditorContainer = styled.div<{ error: boolean }>`
  background-color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      withVar(props.error ? DesignToken.SurfaceAccentEmphasisMinNegative : DesignToken.SurfaceSecondaryDefault)
  };
  display: flex;
  flex-flow: row nowrap;
  padding: 1.429em 0;
  flex-grow: 1;
  padding-left: 1.429em;
  padding-right: 0.857em;
  border-radius: 4px;
  border: 1px
    ${
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
      (props: any) => (props.error ? withVar(DesignToken.ActionNegativeDefault) : "transparent")
    }
    solid;
`;

export const WorkflowPartEditorGrid = styled.div<{ gridType: WorkflowPartGridType }>`
  display: grid;
  position: relative;
  padding-right: 1em;
  grid-template-columns: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => {
      switch (props.gridType) {
        case WorkflowPartGridType.Standard:
          return "1fr 1fr 1fr";
        case WorkflowPartGridType.WithCollection:
          return "1fr 2fr";
        case WorkflowPartGridType.WithCollectionThree:
          return "1fr 1fr 1fr";
        default:
          return "1fr 1fr 1fr";
      }
    }
  };
  grid-column-gap: 0.571em;
  grid-row-gap: 0.571em;
  flex-grow: 1;

  & > * {
    min-height: 40px;
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.gridType === WorkflowPartGridType.WithCollection &&
      css`
        & > *:nth-child(3) {
          grid-column: 1 / 4;
        }
      `
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.gridType === WorkflowPartGridType.WithCollectionThree &&
      css`
        & > *:nth-child(4) {
          grid-column: 1 / 4;
        }
      `
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.gridType === WorkflowPartGridType.WithCollectionFour &&
      css`
        & > *:nth-child(5) {
          grid-column: 1 / 4;
        }
      `
  }

  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.gridType === WorkflowPartGridType.Message &&
      css`
        & > .action-switcher {
          grid-column: 1;
          grid-row: 1;
        }
        & > .message {
          grid-column: 1 / 4;
        }
        & > .message-action-settings {
          grid-column: 1 / 4;
          grid-row: 2;
        }
      `
  }

  * {
    min-width: 0;
  }
`;

export const StyledButtonGroup = styled.div`
  button {
    margin-right: 8px;
  }

  margin-bottom: 40px;
`;

export const FormBlock = styled(Block)`
  .lc-text-field input,
  .lc-text-field textarea {
    width: 100%;
    max-width: 400px;
  }

  .lc-text-field textarea {
    min-height: 8em;
  }
`;

export const RuleEditorContainer = styled.div`
  max-width: 700px;
  padding-left: ${withVar(SpacingToken.Spacing12)};
  margin: 0 auto;

  hr {
    display: block;
    box-shadow: ${withVar(ShadowToken.DividerBottom)};
    max-width: 60%;
    height: 1px;
    margin: ${withVar(SpacingToken.Spacing10)} 0 ${withVar(SpacingToken.Spacing6)};
    outline: none;
    border: none;
  }
`;

export const StyledSelect = styled(Select)`
  .lc-select-body__list {
    max-height: 280px;
  }

  .item-label {
    overflow: hidden;
    min-width: 0;
  }
`;

export const StyledMultiSelect = styled(MultiSelect)`
  .lc-multiselect-head__items {
    min-height: 34px;
  }

  .lc-multiselect-head {
    min-height: 40px;
  }

  .lc-multiselect-head__items-empty {
    padding-left: 7px;
  }

  .lc-multiselect__dropdown-icon {
    margin-top: 7px;
    margin-right: 10px;
  }
  // this is hacky, but this DS component doesn't allow for color customization
  .lc-multiselect-head__item {
    background-color: ${withVar(DesignToken.SurfaceTertiaryDefault)};
    color: ${withVar(DesignToken.ContentBasicPrimary)};
  }
  .lc-multiselect-head__item-remove-icon {
    fill: ${withVar(DesignToken.ContentBasicSecondary)};
    opacity: unset;
  }
`;

export const StyledInput = styled(Input)`
  width: 100%;
  min-height: 40px;
`;

export const ErrorText = styled.span`
  font-size: 13px;
  line-height: 16px;
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.theme.errorColor
  };
  height: 0px;
  display: block;
  overflow: visible;
  padding-top: 2px;
`;

export const StyledTextAreaAutosizeField = styled(TextAreaAutosizeField)`
  flex: 1 1;
`;

export const StyledText = styled(Text)`
  &.disabled {
    opacity: 0.5;
  }
`;

export const StyledFlex = styled(Flex)`
  .lc-input {
    max-width: 50%;
    margin-right: 8px;
  }

  .lc-select {
    max-width: 50%;
  }

  & > * {
    flex: 1 0;
    box-sizing: border-box;
    flex-basis: calc(100% - 20px);
  }
`;

export const QuantifierSelect = styled(Select)`
  min-width: 90px;
`;

export const ActionLink = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: var(${SpacingToken.Spacing4}) var(${SpacingToken.Spacing5});
  cursor: pointer;
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  border-radius: var(${RadiusToken.Radius3});
  transition: border-color var(${TransitionDurationToken.Fast2}) var(${TransitionTimingToken.EaseOut});

  &:hover {
    border-color: ${withVar(DesignToken.BorderBasicHover)};
  }
`;
